import {
  type FinancialAidRequirementField,
  type FinancialAidRequirementPolicy,
  resolveFinancialAidRequirementState,
} from '@triedge/staff-settings-domain';
import {
  isValidFinancialAidParentEmail,
  isValidFinancialAidSocialSecurityNumber,
} from '@triedge/financial-aid-domain/value-objects';
import { resolveFinancialAidDisplayYears } from '../../../financial-aid/financialAidAcademicYear';
import { FINANCIAL_AID_REQUIREMENT_FIELDS } from '../../../financial-aid/financialAidRequirementFields';
import {
  isParentGuardianFormKeyActive,
  resolveParentGuardianActiveKeys,
  resolveParentGuardianHouseholdComposition,
  type ParentGuardianHouseholdComposition,
} from '../../../shared/parentGuardianHousehold';
import { PARENT_FINANCIAL_AID_ATTESTATION_KEYS } from './financialAidAttestations';

export type ParentFinancialAidValidationIssue = {
  key: string;
  label: string;
  message: string;
  step: number;
  kind: 'missing' | 'invalid';
};

type FieldRule = {
  key: string;
  label: string;
  step: number;
  message?: string;
};

type ParentKey = 'father' | 'mother';
type NumericFieldRule = FieldRule & {
  max?: number;
  integer?: boolean;
};
type ArrayNumericFieldRule = {
  arrayKey: string;
  fieldKey: string;
  label: string;
  step: number;
  max?: number;
};
type ParentFinancialAidValidationOptions = {
  academicYear?: unknown;
  requirementPolicy?: FinancialAidRequirementPolicy;
  householdComposition?: ParentGuardianHouseholdComposition;
  certificationStatements?: Record<string, string>;
};

const MAX_PARENT_FINANCIAL_AID_AMOUNT = 100_000_000;

const PARENT_REQUIRED_TEXT_FIELDS: Record<ParentKey, FieldRule[]> = {
  father: [
    { key: 'father.firstName', label: "Father's first name", step: 0 },
    { key: 'father.lastName', label: "Father's last name", step: 0 },
    { key: 'father.cellPhone', label: "Father's cell phone", step: 0 },
    { key: 'father.email', label: "Father's email", step: 0 },
    { key: 'father.address', label: 'Home address', step: 0 },
    { key: 'father.city', label: 'City', step: 0 },
    { key: 'father.state', label: 'State', step: 0 },
    { key: 'father.zip', label: 'ZIP code', step: 0 },
  ],
  mother: [
    { key: 'mother.firstName', label: "Mother's first name", step: 0 },
    { key: 'mother.lastName', label: "Mother's last name", step: 0 },
    { key: 'mother.cellPhone', label: "Mother's cell phone", step: 0 },
    { key: 'mother.email', label: "Mother's email", step: 0 },
    { key: 'mother.address', label: "Mother's home address", step: 0 },
    { key: 'mother.city', label: "Mother's city", step: 0 },
    { key: 'mother.state', label: "Mother's state", step: 0 },
    { key: 'mother.zip', label: "Mother's ZIP code", step: 0 },
  ],
};

// request.offeredTuition moved to the configurable requirement registry
// (default 'required'); schools may relax or hide it via settings.
const REQUIRED_TEXT_FIELDS: FieldRule[] = [
  { key: 'expenses.housing', label: 'Monthly housing expense', step: 3 },
  { key: 'expenses.utilities', label: 'Monthly utilities', step: 3 },
  { key: 'expenses.food', label: 'Monthly food expense', step: 3 },
  { key: 'contract.totalTuition', label: 'Total tuition', step: 6 },
  { key: 'contract.totalFees', label: 'Total fees', step: 6 },
  { key: 'contract.eligibleTuition', label: 'Less Preschool tuition', step: 6 },
  { key: 'request.requestedAmount', label: 'Scholarship requested', step: 6 },
];

const NUMERIC_FIELD_RULES: NumericFieldRule[] = [
  { key: 'incomeFather.gross2024', label: 'Father gross income', step: 1 },
  { key: 'incomeMother.gross2024', label: 'Mother gross income', step: 1 },
  { key: 'incomeFather.parsonage', label: 'Father parsonage', step: 1 },
  { key: 'incomeMother.parsonage', label: 'Mother parsonage', step: 1 },
  { key: 'incomeFather.refunds', label: 'Father tax refunds / credits', step: 1 },
  { key: 'incomeMother.refunds', label: 'Mother tax refunds / credits', step: 1 },
  { key: 'incomeFather.estimated2025', label: 'Father estimated income', step: 1 },
  { key: 'incomeMother.estimated2025', label: 'Mother estimated income', step: 1 },
  { key: 'benefits.healthInsurance', label: 'Employer paid health insurance', step: 1 },
  { key: 'benefits.tuition', label: 'Employer paid tuition', step: 1 },
  { key: 'benefits.auto', label: 'Auto allowance', step: 1 },
  { key: 'benefits.other', label: 'Other company benefits', step: 1 },
  { key: 'benefits.giftsAmount', label: 'Gift or funding amount', step: 1 },
  { key: 'assets.purchasePrice', label: 'Purchase price', step: 2 },
  { key: 'assets.mortgageBalance', label: 'Mortgage balance', step: 2 },
  { key: 'assets.mortgagePayment', label: 'Monthly mortgage payment', step: 2 },
  { key: 'assets.mortgageYears', label: 'Total years of mortgage', step: 2, max: 100, integer: true },
  { key: 'assets.propertyTaxes', label: 'Property taxes', step: 2 },
  { key: 'assets.interestRate', label: 'Interest rate', step: 2, max: 100 },
  { key: 'assets.rentPayment', label: 'Monthly rent', step: 2 },
  { key: 'assets.utilities', label: 'Utilities', step: 2 },
  { key: 'savings.balance', label: 'Savings balance', step: 2 },
  { key: 'checking.balance', label: 'Checking balance', step: 2 },
  { key: 'expenses.housing', label: 'Monthly housing expense', step: 3 },
  { key: 'expenses.utilities', label: 'Monthly utilities', step: 3 },
  { key: 'expenses.studentLoans', label: 'Student loans', step: 3 },
  { key: 'expenses.food', label: 'Monthly food expense', step: 3 },
  { key: 'expenses.tuition', label: 'Other tuition expense', step: 3 },
  { key: 'expenses.bankLoans', label: 'Bank loans', step: 3 },
  { key: 'expenses.lifeInsurance', label: 'Life insurance', step: 3 },
  { key: 'expenses.shulDues', label: 'Shul dues', step: 3 },
  { key: 'expenses.carInsurance', label: 'Car insurance', step: 3 },
  { key: 'expenses.healthInsurance', label: 'Health insurance', step: 3 },
  { key: 'expenses.cleaningHelp', label: 'Cleaning help', step: 3 },
  { key: 'expenses.other', label: 'Other expenses', step: 3 },
  { key: 'majorExp.homeImprovements', label: 'Home improvements', step: 5 },
  { key: 'majorExp.largePurchases', label: 'Large purchases', step: 5 },
  { key: 'majorExp.simchaCost', label: 'Simcha cost', step: 5 },
  { key: 'majorExp.loanAmount', label: 'Loan amount', step: 5 },
  { key: 'vacation.cost', label: 'Vacation cost', step: 5 },
  { key: 'contract.totalTuition', label: 'Total tuition', step: 6 },
  { key: 'contract.totalFees', label: 'Total fees', step: 6 },
  { key: 'contract.preschoolTuition', label: 'Preschool tuition', step: 6 },
  { key: 'contract.eligibleTuition', label: 'Less Preschool tuition', step: 6 },
  { key: 'request.requestedAmount', label: 'Scholarship requested', step: 6 },
  { key: 'request.offeredTuition', label: 'Family tuition offer', step: 6 },
  { key: 'request.offeredFees', label: 'Offered fees', step: 6 },
];

const ARRAY_NUMERIC_FIELD_RULES: ArrayNumericFieldRule[] = [
  { arrayKey: 'vehicles', fieldKey: 'value', label: 'Vehicle value', step: 2 },
  { arrayKey: 'vehicles', fieldKey: 'debt', label: 'Vehicle debt', step: 2 },
  { arrayKey: 'vehicles', fieldKey: 'payment', label: 'Vehicle payment', step: 2 },
  { arrayKey: 'investments', fieldKey: 'value', label: 'Investment value', step: 2 },
  { arrayKey: 'childrenAssets', fieldKey: 'value', label: 'Child asset value', step: 2 },
  { arrayKey: 'otherChildren', fieldKey: 'fullTuition', label: 'Other child tuition', step: 4 },
  { arrayKey: 'otherChildren', fieldKey: 'scholarship', label: 'Other child scholarship', step: 4 },
  { arrayKey: 'camps2025', fieldKey: 'fee', label: 'Current-year camp fee', step: 5 },
  { arrayKey: 'camps2024', fieldKey: 'fee', label: 'Prior-year camp fee', step: 5 },
];

const buildNumericFieldRules = (options: ParentFinancialAidValidationOptions = {}): NumericFieldRule[] => {
  const displayYears = resolveFinancialAidDisplayYears(options.academicYear);
  return NUMERIC_FIELD_RULES.map((rule) => {
    if (rule.key === 'incomeFather.gross2024') {
      return {
        ...rule,
        label: displayYears.actualYear ? `Father gross income ${displayYears.actualYear}` : rule.label,
      };
    }
    if (rule.key === 'incomeMother.gross2024') {
      return {
        ...rule,
        label: displayYears.actualYear ? `Mother gross income ${displayYears.actualYear}` : rule.label,
      };
    }
    return rule;
  });
};

const buildArrayNumericFieldRules = (options: ParentFinancialAidValidationOptions = {}): ArrayNumericFieldRule[] => {
  const displayYears = resolveFinancialAidDisplayYears(options.academicYear);
  return ARRAY_NUMERIC_FIELD_RULES.map((rule) => {
    if (rule.arrayKey === 'camps2025') {
      return {
        ...rule,
        label: displayYears.currentCampYear ? `Camp ${displayYears.currentCampYear} fee` : rule.label,
      };
    }
    if (rule.arrayKey === 'camps2024') {
      return {
        ...rule,
        label: displayYears.priorCampYear ? `Camp ${displayYears.priorCampYear} fee` : rule.label,
      };
    }
    return rule;
  });
};

const buildAttestationRules = (
  options: ParentFinancialAidValidationOptions = {},
): FieldRule[] => PARENT_FINANCIAL_AID_ATTESTATION_KEYS.map((key) => ({
  key: `signatures.attestations.${key}`,
  label: key === 'certifiedAccurate'
    ? 'Accuracy attestation'
    : key === 'authorizedVerification'
      ? 'Verification authorization'
      : 'Income-change agreement',
  step: 8,
  // Schools can replace the displayed statement, so the reminder must not
  // restate the default wording in that case.
  message: readText(options.certificationStatements?.[key])
    ? 'Please confirm the certification statement before submitting.'
    : key === 'certifiedAccurate'
      ? 'Confirm that the information provided is true and complete.'
      : key === 'authorizedVerification'
        ? 'Authorize verification with employers and banks.'
        : 'Agree to report income changes within 30 days.',
}));

const VALIDATION_FIELD_ORDER = [
  'father.firstName',
  'father.lastName',
  'father.cellPhone',
  'father.email',
  'father.ssn',
  'father.address',
  'father.city',
  'father.state',
  'father.zip',
  'father.unemploymentBenefits',
  'mother.firstName',
  'mother.lastName',
  'mother.cellPhone',
  'mother.email',
  'mother.ssn',
  'mother.address',
  'mother.city',
  'mother.state',
  'mother.zip',
  'mother.unemploymentBenefits',
  'income.householdGross2024',
  'benefits.giftsAmount',
  'assets.purchasePrice',
  'assets.mortgagePayment',
  'assets.rentPayment',
  'savings.balance',
  'checking.balance',
  'expenses.housing',
  'expenses.utilities',
  'expenses.food',
  'contract.childrenCount',
  'contract.totalTuition',
  'contract.totalFees',
  'contract.eligibleTuition',
  'request.requestedAmount',
  'request.offeredTuition',
  'documents',
  ...PARENT_FINANCIAL_AID_ATTESTATION_KEYS.map((key) => `signatures.attestations.${key}`),
  'signatures.father',
  'signatures.mother',
] as const;

const VALIDATION_FIELD_ORDER_INDEX = new Map<string, number>(
  VALIDATION_FIELD_ORDER.map((key, index) => [key, index]),
);

const getValidationIssueOrder = (issue: ParentFinancialAidValidationIssue): number => {
  const exactOrder = VALIDATION_FIELD_ORDER_INDEX.get(issue.key);
  if (exactOrder !== undefined) return exactOrder;
  if (issue.key.startsWith('documents.')) {
    return VALIDATION_FIELD_ORDER_INDEX.get('documents') ?? issue.step * 100;
  }
  return VALIDATION_FIELD_ORDER.length + issue.step * 100;
};

const sortValidationIssuesByFormOrder = (
  issues: ParentFinancialAidValidationIssue[],
): ParentFinancialAidValidationIssue[] => (
  issues
    .map((issue, index) => ({ issue, index }))
    .sort((left, right) => {
      const orderDelta = getValidationIssueOrder(left.issue) - getValidationIssueOrder(right.issue);
      if (orderDelta !== 0) return orderDelta;
      return left.index - right.index;
    })
    .map(({ issue }) => issue)
);

const hasText = (value: unknown): boolean => String(value ?? '').trim().length > 0;
const readText = (value: unknown): string => String(value ?? '').trim();
const isValidEmail = isValidFinancialAidParentEmail;
const isValidZip = (value: unknown): boolean => /^\d{5}(-\d{4})?$/.test(readText(value));
const isLikelyPlaceholderPhone = (digits: string): boolean =>
  /^(\d)\1{9,}$/.test(digits)
  || digits === '1234567890'
  || digits === '9876543210'
  || digits === '0123456789';

const isValidPhone = (value: unknown): boolean => {
  const normalized = readText(value);
  const digits = normalized.replace(/\D/g, '');
  return /^[\d\s\-().+]+$/.test(normalized)
    && digits.length >= 10
    && !isLikelyPlaceholderPhone(digits);
};
const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const readPath = (source: unknown, path: string): unknown => {
  return path.split('.').reduce<unknown>((current, part) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[part];
  }, source);
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

const resolveValidationHouseholdComposition = (
  data: unknown,
  options: ParentFinancialAidValidationOptions = {},
): ParentGuardianHouseholdComposition =>
  options.householdComposition ?? resolveParentGuardianHouseholdComposition(data);

const resolveFieldParentKey = (fieldKey: string): ParentKey | null => {
  if (fieldKey.startsWith('father.') || fieldKey.startsWith('incomeFather.')) return 'father';
  if (fieldKey.startsWith('mother.') || fieldKey.startsWith('incomeMother.')) return 'mother';
  return null;
};

const isFieldActiveForHouseholdComposition = (
  composition: ParentGuardianHouseholdComposition,
  fieldKey: string,
): boolean => {
  const parentKey = resolveFieldParentKey(fieldKey);
  return !parentKey || isParentGuardianFormKeyActive(composition, parentKey);
};

const hasIssueForKey = (
  issues: ParentFinancialAidValidationIssue[],
  key: string,
): boolean => issues.some((issue) => issue.key === key);

const hasDistinctParentText = (
  data: unknown,
  parentKey: ParentKey,
  field: string,
): boolean => {
  const value = readText(readPath(data, `${parentKey}.${field}`));
  if (!value) return false;
  const otherParentKey: ParentKey = parentKey === 'father' ? 'mother' : 'father';
  const otherValue = readText(readPath(data, `${otherParentKey}.${field}`));
  return value.toLowerCase() !== otherValue.toLowerCase();
};

const pushMissing = (
  issues: ParentFinancialAidValidationIssue[],
  rule: FieldRule,
  message = `${rule.label} is required.`,
): void => {
  issues.push({
    key: rule.key,
    label: rule.label,
    message,
    step: rule.step,
    kind: 'missing',
  });
};

const pushInvalid = (
  issues: ParentFinancialAidValidationIssue[],
  key: string,
  label: string,
  step: number,
  message: string,
): void => {
  issues.push({ key, label, message, step, kind: 'invalid' });
};

const isParentKey = (value: unknown): value is ParentKey =>
  value === 'father' || value === 'mother';

const isPolicyConditionApplicable = (
  data: unknown,
  field: FinancialAidRequirementField,
): boolean => {
  const condition = field.condition;
  if (condition.kind === 'always') return true;
  if (condition.kind === 'parentIsActive' && isParentKey(condition.parentKey)) {
    return shouldParentFinancialAidParentShowRequiredLabels(data, condition.parentKey)
      || hasAnyParentRequiredText(data, condition.parentKey);
  }
  if (condition.kind === 'housingType' && typeof condition.value === 'string') {
    return (readText(readPath(data, 'assets.housingType')) || 'own') === condition.value;
  }
  if (condition.kind === 'booleanPath' && typeof condition.path === 'string') {
    return readPath(data, condition.path) === condition.value;
  }
  if (condition.kind === 'arrayRow' && typeof condition.arrayKey === 'string') {
    return Array.isArray(readPath(data, condition.arrayKey));
  }
  return false;
};

const REQUIREMENT_FIELD_BY_KEY = new Map(
  FINANCIAL_AID_REQUIREMENT_FIELDS.map((field) => [field.fieldKey, field]),
);

export const shouldParentFinancialAidShowConfiguredRequiredLabel = (
  data: unknown,
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
  fieldKey: string,
): boolean => {
  const field = REQUIREMENT_FIELD_BY_KEY.get(fieldKey);
  return Boolean(
    field
      && resolveFinancialAidRequirementState(requirementPolicy, field) === 'required'
      && isPolicyConditionApplicable(data, field),
  );
};

export const shouldParentFinancialAidShowConfiguredField = (
  requirementPolicy: FinancialAidRequirementPolicy | undefined,
  fieldKey: string,
): boolean => {
  const field = REQUIREMENT_FIELD_BY_KEY.get(fieldKey);
  return !field || resolveFinancialAidRequirementState(requirementPolicy, field) !== 'hidden';
};

const pushConfiguredRequirementIssues = (
  data: unknown,
  issues: ParentFinancialAidValidationIssue[],
  options: ParentFinancialAidValidationOptions,
): void => {
  FINANCIAL_AID_REQUIREMENT_FIELDS.forEach((field) => {
    if (resolveFinancialAidRequirementState(options.requirementPolicy, field) !== 'required') return;
    if (!isFieldActiveForHouseholdComposition(resolveParentGuardianHouseholdComposition(data), field.fieldKey)) return;
    if (!isPolicyConditionApplicable(data, field)) return;
    if (hasText(readPath(data, field.path))) return;
    if (hasIssueForKey(issues, field.fieldKey)) return;
    pushMissing(issues, {
      key: field.fieldKey,
      label: field.label,
      step: field.step,
      message: field.message,
    }, field.message ?? `${field.label} is required.`);
  });
};

const validateNumericField = (
  data: unknown,
  issues: ParentFinancialAidValidationIssue[],
  rule: NumericFieldRule,
): void => {
  const raw = readText(readPath(data, rule.key));
  if (!raw) return;
  const normalized = raw.replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    pushInvalid(issues, rule.key, rule.label, rule.step, `Enter a valid ${rule.integer ? 'whole number' : 'amount'} for ${rule.label}.`);
    return;
  }
  const parsed = Number(normalized);
  if (rule.integer && !Number.isInteger(parsed)) {
    pushInvalid(issues, rule.key, rule.label, rule.step, `Enter a valid whole number for ${rule.label}.`);
    return;
  }
  const max = rule.max ?? MAX_PARENT_FINANCIAL_AID_AMOUNT;
  if (!Number.isFinite(parsed) || parsed > max || parsed > Number.MAX_SAFE_INTEGER) {
    pushInvalid(issues, rule.key, rule.label, rule.step, `Enter a smaller amount for ${rule.label}.`);
  }
};

const validateArrayNumericFields = (
  data: unknown,
  issues: ParentFinancialAidValidationIssue[],
  rule: ArrayNumericFieldRule,
): void => {
  const rows = readPath(data, rule.arrayKey);
  if (!Array.isArray(rows)) return;
  rows.forEach((row, index) => {
    const raw = readText((row as Record<string, unknown> | null | undefined)?.[rule.fieldKey]);
    if (!raw) return;
    const key = `${rule.arrayKey}.${index}.${rule.fieldKey}`;
    const normalized = raw.replace(/,/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
      pushInvalid(issues, key, rule.label, rule.step, `Enter a valid amount for ${rule.label}.`);
      return;
    }
    const parsed = Number(normalized);
    const max = rule.max ?? MAX_PARENT_FINANCIAL_AID_AMOUNT;
    if (!Number.isFinite(parsed) || parsed > max || parsed > Number.MAX_SAFE_INTEGER) {
      pushInvalid(issues, key, rule.label, rule.step, `Enter a smaller amount for ${rule.label}.`);
    }
  });
};

export const isParentFinancialAidParentInfoComplete = (
  data: unknown,
  parentKey: ParentKey,
): boolean => {
  const email = readPath(data, `${parentKey}.email`);
  const phone = readPath(data, `${parentKey}.cellPhone`);
  const zip = readPath(data, `${parentKey}.zip`);
  return PARENT_REQUIRED_TEXT_FIELDS[parentKey].every((rule) => hasText(readPath(data, rule.key)))
    && isValidEmail(email)
    && isValidPhone(phone)
    && isValidZip(zip);
};

const hasAnyParentRequiredText = (data: unknown, parentKey: ParentKey): boolean => {
  const parentSpecificKeys = [
    `${parentKey}.title`,
    `${parentKey}.firstName`,
    `${parentKey}.cellPhone`,
    `${parentKey}.homePhone`,
    `${parentKey}.email`,
    `${parentKey}.occupation`,
    `${parentKey}.employer`,
    `${parentKey}.businessName`,
    `${parentKey}.shul`,
    `${parentKey}.rabbi`,
    `${parentKey}.ssn`,
    `${parentKey}.unemploymentBenefits`,
  ];
  if (parentSpecificKeys.some((key) => hasText(readPath(data, key)))) return true;
  if (readPath(data, `${parentKey}.isUnemployed`) === true) return true;
  if (readPath(data, `${parentKey}.isDivorced`) === true) return true;
  return ['lastName', 'address', 'city', 'state', 'zip']
    .some((field) => hasDistinctParentText(data, parentKey, field));
};

export const shouldParentFinancialAidParentShowRequiredLabels = (
  data: unknown,
  parentKey: ParentKey,
): boolean => {
  const householdComposition = resolveParentGuardianHouseholdComposition(data);
  if (!isParentGuardianFormKeyActive(householdComposition, parentKey)) return false;
  if (householdComposition.mode === 'single_parent_guardian') return true;
  const fatherComplete = isParentFinancialAidParentInfoComplete(data, 'father');
  const motherComplete = isParentFinancialAidParentInfoComplete(data, 'mother');
  if (!fatherComplete && !motherComplete) return true;
  return parentKey === 'father' ? fatherComplete : motherComplete;
};

export const shouldParentFinancialAidParentRequireSignature = (
  data: unknown,
  parentKey: ParentKey,
): boolean => {
  const householdComposition = resolveParentGuardianHouseholdComposition(data);
  if (!isParentGuardianFormKeyActive(householdComposition, parentKey)) return false;
  if (householdComposition.mode === 'single_parent_guardian') return true;
  const fatherComplete = isParentFinancialAidParentInfoComplete(data, 'father');
  const motherComplete = isParentFinancialAidParentInfoComplete(data, 'mother');
  if (!fatherComplete && !motherComplete) return true;
  return parentKey === 'father' ? fatherComplete : motherComplete;
};

export const getParentFinancialAidValidationIssues = (
  data: unknown,
  options: ParentFinancialAidValidationOptions = {},
): ParentFinancialAidValidationIssue[] => {
  const issues: ParentFinancialAidValidationIssue[] = [];
  const dataWithHouseholdComposition = {
    ...asRecord(data),
    householdComposition: resolveValidationHouseholdComposition(data, options),
  };
  const activeParentKeys = resolveParentGuardianActiveKeys(dataWithHouseholdComposition.householdComposition);
  const anyActiveParentInfoComplete = activeParentKeys.some((parentKey) =>
    isParentFinancialAidParentInfoComplete(data, parentKey),
  );

  if (!anyActiveParentInfoComplete) {
    activeParentKeys.forEach((parentKey) => {
      PARENT_REQUIRED_TEXT_FIELDS[parentKey].forEach((rule) => {
        if (!hasText(readPath(data, rule.key))) {
          pushMissing(issues, rule);
        }
      });
    });
  } else {
    activeParentKeys.forEach((parentKey) => {
      if (isParentFinancialAidParentInfoComplete(data, parentKey)) {
        return;
      }
      if (
        dataWithHouseholdComposition.householdComposition.mode !== 'single_parent_guardian'
        && !hasAnyParentRequiredText(data, parentKey)
      ) {
        return;
      }
      PARENT_REQUIRED_TEXT_FIELDS[parentKey].forEach((rule) => {
        if (!hasText(readPath(data, rule.key))) {
          pushMissing(issues, rule);
        }
      });
    });
  }

  REQUIRED_TEXT_FIELDS.forEach((rule) => {
    if (!hasText(readPath(data, rule.key))) {
      pushMissing(issues, rule);
    }
  });

  activeParentKeys.forEach((parentKey) => {
    const labelPrefix = parentKey === 'father' ? 'Father' : 'Mother';
    const emailKey = `${parentKey}.email`;
    const phoneKey = `${parentKey}.cellPhone`;
    const zipKey = `${parentKey}.zip`;
    const ssnKey = `${parentKey}.ssn`;
    const email = readPath(data, emailKey);
    const phone = readPath(data, phoneKey);
    const zip = readPath(data, zipKey);
    const ssn = readPath(data, ssnKey);
    if (hasText(email) && !isValidEmail(email)) {
      pushInvalid(issues, emailKey, `${labelPrefix} email`, 0, 'Enter a valid email address.');
    }
    if (hasText(phone) && !isValidPhone(phone)) {
      pushInvalid(issues, phoneKey, `${labelPrefix} cell phone`, 0, 'Enter a valid phone number.');
    }
    if (hasText(zip) && !isValidZip(zip)) {
      pushInvalid(issues, zipKey, `${labelPrefix} ZIP code`, 0, 'Enter a valid ZIP code.');
    }
    if (hasText(ssn) && !isValidFinancialAidSocialSecurityNumber(ssn)) {
      pushInvalid(issues, ssnKey, `${labelPrefix} Social Security number`, 0, 'Enter a Social Security number in 123-45-6789 format.');
    }
  });

  activeParentKeys.forEach((parentKey) => {
    if (readPath(data, `${parentKey}.isUnemployed`) === true && !hasText(readPath(data, `${parentKey}.unemploymentBenefits`))) {
      pushMissing(issues, {
        key: `${parentKey}.unemploymentBenefits`,
        label: `${parentKey === 'father' ? 'Father' : 'Mother'} unemployment benefits`,
        step: 0,
      });
    }
  });

  pushConfiguredRequirementIssues(dataWithHouseholdComposition, issues, options);

  buildNumericFieldRules(options).forEach((rule) => {
    if (!isFieldActiveForHouseholdComposition(dataWithHouseholdComposition.householdComposition, rule.key)) return;
    validateNumericField(data, issues, rule);
  });
  buildArrayNumericFieldRules(options).forEach((rule) => {
    validateArrayNumericFields(data, issues, rule);
  });

  const activeIncomeEntered = activeParentKeys.some((parentKey) => {
    const key = parentKey === 'father' ? 'incomeFather.gross2024' : 'incomeMother.gross2024';
    return hasText(readPath(data, key));
  });
  // When the school configures per-parent gross income as required, the
  // registry issues above already cover the household baseline.
  const grossConfiguredRequired = activeParentKeys.some((parentKey) => {
    const fieldKey = parentKey === 'father' ? 'incomeFather.gross2024' : 'incomeMother.gross2024';
    const field = REQUIREMENT_FIELD_BY_KEY.get(fieldKey);
    return Boolean(field
      && resolveFinancialAidRequirementState(options.requirementPolicy, field) === 'required');
  });
  if (!activeIncomeEntered && !grossConfiguredRequired) {
    pushMissing(issues, {
      key: 'income.householdGross2024',
      label: 'Household gross income',
      step: 1,
      message: 'Enter at least one parent gross income amount. Enter 0 if there was no income.',
    }, 'Enter at least one parent gross income amount. Enter 0 if there was no income.');
  }

  if (readPath(data, 'benefits.gifts') === true && !hasText(readPath(data, 'benefits.giftsAmount'))) {
    pushMissing(issues, { key: 'benefits.giftsAmount', label: 'Gift or funding amount', step: 1 });
  }

  const housingType = readText(readPath(data, 'assets.housingType')) || 'own';
  if (housingType === 'rent') {
    if (!hasText(readPath(data, 'assets.rentPayment'))) {
      pushMissing(issues, { key: 'assets.rentPayment', label: 'Monthly rent', step: 2 });
    }
  } else {
    ['purchasePrice', 'mortgagePayment'].forEach((field) => {
      const label = field === 'purchasePrice' ? 'Purchase price' : 'Monthly mortgage payment';
      const key = `assets.${field}`;
      if (!hasText(readPath(data, key))) {
        pushMissing(issues, { key, label, step: 2 });
      }
    });
  }

  ['savings.balance', 'checking.balance'].forEach((key) => {
    if (!hasText(readPath(data, key))) {
      pushMissing(issues, {
        key,
        label: key.startsWith('savings') ? 'Savings balance' : 'Checking balance',
        step: 2,
        message: `${key.startsWith('savings') ? 'Savings balance' : 'Checking balance'} is required. Enter 0 if none.`,
      }, `${key.startsWith('savings') ? 'Savings balance' : 'Checking balance'} is required. Enter 0 if none.`);
    }
  });

  if (toNumber(readPath(data, 'contract.childrenCount')) <= 0) {
    pushMissing(issues, {
      key: 'contract.childrenCount',
      label: 'YST children count',
      step: 6,
      message: 'Enter the number of YST children included in this aid request.',
    }, 'Enter the number of YST children included in this aid request.');
  }

  const documents = readPath(data, 'documents');
  if (Array.isArray(documents)) {
    documents.forEach((document) => {
      const record = document as Record<string, unknown>;
      if (record.required === false) return;
      if (record.uploaded !== true) {
        const id = readText(record.id) || 'document';
        const label = readText(record.name) || 'Required document';
        pushMissing(issues, {
          key: `documents.${id}`,
          label,
          step: 7,
          message: `${label} must be uploaded before submission.`,
        }, `${label} must be uploaded before submission.`);
      }
    });
  }

  buildAttestationRules(options).forEach((rule) => {
    if (readPath(data, rule.key) !== true) {
      pushMissing(issues, rule, rule.message ?? `${rule.label} is required.`);
    }
  });

  if (shouldParentFinancialAidParentRequireSignature(dataWithHouseholdComposition, 'father') && !hasText(readPath(data, 'signatures.father'))) {
    pushMissing(issues, { key: 'signatures.father', label: "Father's signature", step: 8 });
  }
  if (shouldParentFinancialAidParentRequireSignature(dataWithHouseholdComposition, 'mother') && !hasText(readPath(data, 'signatures.mother'))) {
    pushMissing(issues, { key: 'signatures.mother', label: "Mother's signature", step: 8 });
  }

  return sortValidationIssuesByFormOrder(issues);
};

export const getParentFinancialAidStepValidationIssues = (
  data: unknown,
  step: number,
  options: ParentFinancialAidValidationOptions = {},
): ParentFinancialAidValidationIssue[] =>
  getParentFinancialAidValidationIssues(data, options).filter((issue) => issue.step === step);

export const isParentFinancialAidStepComplete = (
  data: unknown,
  step: number,
  options: ParentFinancialAidValidationOptions = {},
): boolean =>
  getParentFinancialAidStepValidationIssues(data, step, options).length === 0;

export const isParentFinancialAidSubmissionComplete = (
  data: unknown,
  options: ParentFinancialAidValidationOptions = {},
): boolean =>
  getParentFinancialAidValidationIssues(data, options).length === 0;

export const getParentFinancialAidFirstIssueStep = (issues: ParentFinancialAidValidationIssue[]): number =>
  issues.reduce((step, issue) => Math.min(step, issue.step), 8);
